import { Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import useAuthStore from "@/store/useAuthStore";

export const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const navItems = [
    { name: "How Inkriv Works", href: "/#how-inkriv-works" },
    { name: "Features", href: "/#features" },
    { name: "Pricing", href: "/#pricing" },
  ];

  const { isLoggedIn } = useAuthStore();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    // Prevent scrolling when menu is open
    if (!isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  };

  // Animation variants
  const menuVariants = {
    closed: {
      opacity: 0,
      height: 0,
      transition: {
        duration: 0.3,
        staggerChildren: 0.05,
        staggerDirection: -1,
        when: "afterChildren",
      },
    },
    open: {
      opacity: 1,
      height: "auto",
      transition: {
        duration: 0.4,
        staggerChildren: 0.1,
        delayChildren: 0.1,
        when: "beforeChildren",
      },
    },
  };

  const itemVariants = {
    closed: {
      opacity: 0,
      y: -10,
      transition: { duration: 0.2 },
    },
    open: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3 },
    },
  };

  const buttonVariants = {
    rest: { scale: 1 },
    hover: { scale: 1.05 },
    tap: { scale: 0.95 },
  };

  return (
    <header
      id="navbar"
      className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-lg bg-black/80 transition-all duration-300`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 text-neutral-25 uppercase">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center"
          >
            <Link to="/" className="flex items-center" aria-label="Inkriv Home">
              <img
                src={"/logo/inkriv-logo-light.svg"}
                alt={"logo"}
                className="h-[40px] w-full"
              />
            </Link>
          </motion.div>

          {/* Desktop Navigation */}
          <nav
            className="hidden md:ml-10 md:flex md:space-x-8 md:mr-5"
            aria-label="Main Navigation"
          >
            {navItems.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 * i }}
                whileHover={{ y: -2 }}
              >
                <Link
                  to={item.href}
                  className="text-sm font-normal transition-colors hover:text-white whitespace-nowrap"
                  aria-label={item.name}
                >
                  {item.name}
                </Link>
              </motion.div>
            ))}
          </nav>

          {/* Desktop CTA buttons */}
          <div className="hidden md:flex items-center space-x-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              whileHover={{ y: -2 }}
            >
              <Link
                to={isLoggedIn ? "app" : "/auth/login"}
                className="text-[14px] font-normal capitalize hover:text-white whitespace-nowap underline"
                aria-label="Login to your account"
              >
                Login
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
            >
              <Link
                to={`/auth/signup`}
                className="inline-flex items-center justify-center whitespace-nowrap px-4 py-2 text-[14px] font-normal text-white bg-secondary-700 rounded-[12px] capitalize"
                aria-label="Start writing with Inkriv"
              >
                Start Writing
              </Link>
            </motion.div>
          </div>

          {/* Mobile menu button */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="md:hidden flex items-center"
          >
            <motion.button
              whileTap={{ scale: 0.9 }}
              type="button"
              className="text-neutral-25 hover:text-white focus:outline-none"
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
              aria-label="Toggle menu"
              onClick={toggleMenu}
            >
              <AnimatePresence mode="wait" initial={false}>
                {!isMenuOpen ? (
                  <motion.svg
                    key="menu"
                    initial={{ opacity: 0, rotate: -90 }}
                    animate={{ opacity: 1, rotate: 0 }}
                    exit={{ opacity: 0, rotate: 90 }}
                    transition={{ duration: 0.2 }}
                    className="h-6 w-6"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </motion.svg>
                ) : (
                  <motion.svg
                    key="close"
                    initial={{ opacity: 0, rotate: 90 }}
                    animate={{ opacity: 1, rotate: 0 }}
                    exit={{ opacity: 0, rotate: -90 }}
                    transition={{ duration: 0.2 }}
                    className="h-6 w-6"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </motion.svg>
                )}
              </AnimatePresence>
            </motion.button>
          </motion.div>
        </div>
      </div>

      {/* Mobile menu with AnimatePresence for smooth entry/exit */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            id="mobile-menu"
            initial="closed"
            animate="open"
            exit="closed"
            variants={menuVariants}
            className="md:hidden bg-black/80 backdrop-blur-lg overflow-hidden"
          >
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              {navItems.map((item, i) => (
                <motion.div
                  key={i}
                  variants={itemVariants}
                  whileHover={{ x: 5 }}
                  transition={{ type: "spring", stiffness: 400 }}
                >
                  <Link
                    to={item.href}
                    className="block px-3 py-2 text-sm font-medium font-satoshi text-neutral-25 hover:text-white"
                    aria-label={item.name}
                    onClick={() => {
                      setIsMenuOpen(false);
                      document.body.style.overflow = "auto";
                    }}
                  >
                    {item.name}
                  </Link>
                </motion.div>
              ))}
            </div>
            <motion.div
              variants={itemVariants}
              className="pt-4 pb-3 border-t border-neutral-700"
            >
              <div className="flex items-center justify-center space-x-4 px-5">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to={isLoggedIn ? "app" : "/auth/login"}
                    className="block px-3 py-2 text-base font-medium text-neutral-25 hover:text-white capitalize font-satoshi"
                    aria-label="Login to your account"
                    onClick={() => {
                      setIsMenuOpen(false);
                      document.body.style.overflow = "auto";
                    }}
                  >
                    Login
                  </Link>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to={`/auth/signup`}
                    className="inline-flex font-satoshi items-center justify-center px-4 py-2 text-[14px] font-normal text-white bg-secondary-700 rounded-[12px] capitalize"
                    aria-label="Start writing with Inkriv"
                    onClick={() => {
                      setIsMenuOpen(false);
                      document.body.style.overflow = "auto";
                    }}
                  >
                    Start Writing
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
